import {
  useGetBackListeElettoraliIdMembri,
  type MembroListaElettorale,
} from '@framework/api-client';
import { useTranslation } from '@framework/i18n';
import { getNetworkErrorMessage, LoaderSpin } from '@framework/ui';
import { Card, message, Radio, Space, theme } from 'antd';
import styled from 'styled-components';
import { generateCandidateLabel } from '../../candidate/form/generateCandidateLabel';
import {
  electoralListChooserMembersParams,
  electoralListChooserMembersRequestOptions,
  firstMemberByOrdine,
} from '../electoralListChooserMembers';
import { ElectoralListChooserListImage } from './ElectoralListChooserListImage';

// Ogni candidato è una riga selezionabile con bordo: diventa primario in hover e
// quando il candidato è selezionato, per rendere evidenti le opzioni.
const CandidateOptions = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  .ant-radio-wrapper {
    width: 100%;
    margin-inline-end: 0;
    padding: 6px 12px;
    border: 1px solid ${props => props.theme.colorBorder};
    border-radius: ${props => props.theme.borderRadius}px;
    transition: border-color 0.2s, background-color 0.2s;
  }

  .ant-radio-wrapper:not(.ant-radio-wrapper-disabled):hover {
    border-color: ${props => props.theme.colorPrimary};
  }

  .ant-radio-wrapper-checked {
    border-color: ${props => props.theme.colorPrimary};
    background-color: ${props => props.theme.controlItemBgActive};
  }
`;

export interface IElectoralListChooserListBoxProps {
  listId: string;
  listName: string;
  selected: boolean;
  selectedMemberId?: string | null;
  columns?: string[];
  disabled?: boolean;
  /**
   * Seleziona/deseleziona la lista. `defaultMember` è il candidato di default
   * (primo per `ordine`) proposto se il votante non ne sceglie uno.
   */
  onSelectList: (listId: string, defaultMember?: MembroListaElettorale) => void;
  onSelectMember: (listId: string, member: MembroListaElettorale) => void;
}

export function ElectoralListChooserListBox(
  props: Readonly<IElectoralListChooserListBoxProps>,
) {
  const {
    listId,
    listName,
    selected,
    selectedMemberId,
    disabled = false,
  } = props;
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const { data: membersData, isLoading } = useGetBackListeElettoraliIdMembri(
    listId,
    electoralListChooserMembersParams,
    {
      ...electoralListChooserMembersRequestOptions,
      query: {
        // In errore il box resterebbe su "Nessun candidato disponibile" senza
        // spiegazioni: segnaliamo con message.error come la tabella membri CTS.
        // La key fissa evita un toast per ogni box quando falliscono tutte le
        // fetch (es. backend irraggiungibile): antd aggiorna il toast esistente.
        onError: error => {
          message.error({
            key: 'electoral-list-chooser-members-error',
            content: getNetworkErrorMessage(
              error,
              t(
                'widgets.electoral-list-chooser.members-load-error',
                'Errore durante il caricamento dei membri della lista elettorale',
              ),
            ),
          });
        },
      },
    },
  );
  const members = (membersData?.dati ?? []).filter(m => m.id);
  const defaultMember = firstMemberByOrdine(members);
  // Una lista senza candidati selezionabili non è votabile: senza candidato non
  // potremmo impostare il default, quindi la rendiamo non selezionabile.
  const hasNoMembers = !isLoading && members.length === 0;

  const memberLabel = (m: MembroListaElettorale) =>
    generateCandidateLabel(m, props.columns);

  const renderCandidates = () => {
    if (isLoading) {
      return <LoaderSpin size="small" />;
    }
    if (hasNoMembers) {
      return (
        <span style={{ color: token.colorTextSecondary }}>
          {t(
            'widgets.electoral-list-chooser.no-candidates',
            'Nessun candidato disponibile',
          )}
        </span>
      );
    }
    return (
      <CandidateOptions
        value={selected ? selectedMemberId ?? undefined : undefined}
        disabled={disabled}
        onChange={e => {
          const member = members.find(m => m.id === e.target.value);
          if (member) props.onSelectMember(listId, member);
        }}
      >
        {members.map(m => (
          <Radio key={m.id} value={m.id}>
            {memberLabel(m)}
          </Radio>
        ))}
      </CandidateOptions>
    );
  };

  return (
    <Card
      hoverable={!disabled && !isLoading}
      style={{
        borderColor: selected ? token.colorPrimary : undefined,
        height: '100%',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Radio
          checked={selected}
          // Disabilitata finché i candidati non sono caricati (così alla
          // selezione il candidato di default è già disponibile) e se la lista
          // non ha candidati selezionabili.
          disabled={disabled || isLoading || hasNoMembers}
          onChange={() => props.onSelectList(listId, defaultMember)}
          onClick={() => {
            if (selected) props.onSelectList(listId, defaultMember);
          }}
        >
          <Space align="center">
            <ElectoralListChooserListImage listId={listId} alt={listName} />
            <strong>{listName}</strong>
          </Space>
        </Radio>
        {renderCandidates()}
      </Space>
    </Card>
  );
}
